import React, { useEffect, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useGetRoutePriceSuggestionQuery } from "../Api/attoDeskApi";
import {
  IRoutePriceSuggestionData,
  IRoutePriceSuggestionTrip,
} from "../Api/Interface/api.interface";

interface PriceSuggestionCardProps {
  pickupCity: string;
  dropoffCity: string;
  busCount?: number;
  onApplyPrice: (price: number) => void;
}

const fmt = (val: string | undefined) =>
  val ? `$${parseFloat(val).toLocaleString("en-CA", { minimumFractionDigits: 2 })}` : "—";

const PriceSuggestionCard: React.FC<PriceSuggestionCardProps> = ({
  pickupCity,
  dropoffCity,
  busCount = 1,
  onApplyPrice,
}) => {
  const buses = Math.max(1, busCount || 1);
  const skip = !pickupCity || !dropoffCity;

  const {
    data,
    isLoading,
    isFetching,
    isError,
  } = useGetRoutePriceSuggestionQuery(
    { pickupCity, dropoffCity },
    { skip }
  );

  // Smart default: average, unless the last price is very recent (≤ 7 days)
  const suggestion = React.useMemo<IRoutePriceSuggestionData | null>(() => {
    if (!data?.data) return null;
    return data.data as IRoutePriceSuggestionData;
  }, [data]);

  const smartPrice = React.useMemo<number | null>(() => {
    if (!suggestion) return null;
    if (suggestion.lastDate) {
      const daysDiff =
        (Date.now() - new Date(suggestion.lastDate).getTime()) /
        (1000 * 60 * 60 * 24);
      if (daysDiff <= 7) return parseFloat(suggestion.lastPrice);
    }
    return parseFloat(suggestion.averagePrice);
  }, [suggestion]);

  if (skip) return null;

  const loading = isLoading || isFetching;

  return (
    <Box
      sx={{
        mt: 1.5,
        mb: 0.5,
        border: "1px solid #bfdbfe",
        borderRadius: 2,
        bgcolor: "#eff6ff",
        overflow: "hidden",
        transition: "all 0.2s ease",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: "#dbeafe",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, flexShrink: 0 }}>💡</Typography>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: "#1d4ed8",
                textTransform: "uppercase",
                letterSpacing: 0.6,
              }}
            >
              Smart Price
            </Typography>
            <Typography
              noWrap
              sx={{ fontSize: 10, color: "#3b82f6", fontWeight: 500 }}
            >
              {pickupCity} → {dropoffCity}
            </Typography>
          </Box>
        </Box>
        {loading && <CircularProgress size={12} sx={{ color: "#3b82f6" }} />}
        {suggestion && !suggestion.isExactMatch && (
          <Typography
            sx={{
              fontSize: 9,
              color: "#2f3891",
              fontStyle: "italic",
              fontWeight: 600,
            }}
          >
            Based on similar routes
          </Typography>
        )}
      </Box>

      {/* Body */}
      <Box sx={{ px: 1.5, py: 1.25 }}>
        {loading && !suggestion && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            <Skeleton variant="text" width="60%" height={18} />
            <Skeleton variant="text" width="45%" height={18} />
            <Skeleton variant="text" width="70%" height={18} />
          </Box>
        )}

        {isError && (
          <Alert severity="warning" sx={{ py: 0.25, fontSize: 11 }}>
            Could not load price history.
          </Alert>
        )}

        {!loading && !isError && !suggestion && (
          <Typography
            sx={{ fontSize: 12, color: "#64748b", fontStyle: "italic" }}
          >
            No historical data available for this route.
          </Typography>
        )}

        {suggestion && (
          <>
            {/* Stats row */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                mb: 1.25,
              }}
            >
              {/* Last Price */}
              <Box>
                <Typography
                  sx={{ fontSize: 9, fontWeight: 600, color: "#64748b", textTransform: "uppercase" }}
                >
                  Last Trip
                </Typography>
                <Typography
                  sx={{ fontSize: 15, fontWeight: 800, color: "#1e293b", lineHeight: 1.2 }}
                >
                  {fmt(String(parseFloat(suggestion.lastPrice) * buses))}
                </Typography>
                {buses > 1 && (
                  <Typography sx={{ fontSize: 9, color: "#94a3b8" }}>
                    {fmt(suggestion.lastPrice)}/bus × {buses}
                  </Typography>
                )}
                {suggestion.lastDate && (
                  <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>
                    {new Date(suggestion.lastDate).toLocaleDateString("en-CA", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </Typography>
                )}
              </Box>

              {/* Average */}
              <Box>
                <Typography
                  sx={{ fontSize: 9, fontWeight: 600, color: "#64748b", textTransform: "uppercase" }}
                >
                  Average
                </Typography>
                <Typography
                  sx={{ fontSize: 15, fontWeight: 800, color: "#1e293b", lineHeight: 1.2 }}
                >
                  {fmt(String(parseFloat(suggestion.averagePrice) * buses))}
                </Typography>
                {buses > 1 && (
                  <Typography sx={{ fontSize: 9, color: "#94a3b8" }}>
                    {fmt(suggestion.averagePrice)}/bus × {buses}
                  </Typography>
                )}
              </Box>

              {/* Range */}
              {suggestion.minPrice !== suggestion.maxPrice && (
                <Box>
                  <Typography
                    sx={{ fontSize: 9, fontWeight: 600, color: "#64748b", textTransform: "uppercase" }}
                  >
                    Range
                  </Typography>
                  <Typography
                    sx={{ fontSize: 13, fontWeight: 700, color: "#475569", lineHeight: 1.2 }}
                  >
                    {fmt(String(parseFloat(suggestion.minPrice) * buses))} – {fmt(String(parseFloat(suggestion.maxPrice) * buses))}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Recent Trips */}
            {suggestion.recentTrips && suggestion.recentTrips.length > 0 && (
              <>
                <Typography
                  sx={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    mb: 0.5,
                  }}
                >
                  Recent Trips
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, mb: 1.25 }}>
                  {suggestion.recentTrips.map((trip: IRoutePriceSuggestionTrip, i: number) => (
                    <Box
                      key={i}
                      sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                    >
                      <Typography sx={{ fontSize: 11, color: "#475569" }}>
                        {trip.date
                          ? new Date(trip.date).toLocaleDateString("en-CA", {
                              month: "short",
                              day: "numeric",
                            })
                          : "—"}
                        {trip.quoteWorkOrderNo && (
                          <Typography
                            component="span"
                            sx={{ fontSize: 10, color: "#94a3b8", ml: 0.5 }}
                          >
                            · {trip.quoteWorkOrderNo}
                          </Typography>
                        )}
                      </Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>
                        {fmt(String(parseFloat(trip.price) * buses))}
                        {buses > 1 && (
                          <Typography component="span" sx={{ fontSize: 9, color: "#94a3b8", ml: 0.5 }}>
                            ({fmt(trip.price)}/bus)
                          </Typography>
                        )}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}

            <Divider sx={{ mb: 1 }} />

            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
              <Tooltip title={`Apply last trip price: ${fmt(suggestion.lastPrice)}/bus${buses > 1 ? ` × ${buses} = ${fmt(String(parseFloat(suggestion.lastPrice) * buses))} total` : ""}`}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onApplyPrice(parseFloat(suggestion.lastPrice))}
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    py: 0.25,
                    px: 1,
                    borderColor: "#93c5fd",
                    color: "#1d4ed8",
                    textTransform: "none",
                    "&:hover": { bgcolor: "#dbeafe", borderColor: "#3b82f6" },
                  }}
                >
                  Apply Last Price
                </Button>
              </Tooltip>

              <Tooltip title={`Apply average price: ${fmt(suggestion.averagePrice)}/bus${buses > 1 ? ` × ${buses} = ${fmt(String(parseFloat(suggestion.averagePrice) * buses))} total` : ""}`}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onApplyPrice(parseFloat(suggestion.averagePrice))}
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    py: 0.25,
                    px: 1,
                    borderColor: "#93c5fd",
                    color: "#1d4ed8",
                    textTransform: "none",
                    "&:hover": { bgcolor: "#dbeafe", borderColor: "#3b82f6" },
                  }}
                >
                  Apply Average
                </Button>
              </Tooltip>

              {smartPrice !== null && (
                <Tooltip
                  title={`Smart suggestion: ${fmt(String(smartPrice))}/bus${buses > 1 ? ` × ${buses} = ${fmt(String(smartPrice * buses))} total` : ""} — ${
                    smartPrice === parseFloat(suggestion.lastPrice)
                      ? "last price (recent trip)"
                      : "average price"
                  }`}
                >
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => onApplyPrice(smartPrice)}
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      py: 0.25,
                      px: 1,
                      bgcolor: "#2563eb",
                      textTransform: "none",
                      "&:hover": { bgcolor: "#1d4ed8" },
                    }}
                  >
                    ✨ Apply Suggested
                  </Button>
                </Tooltip>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default PriceSuggestionCard;
